/**
 * Draft Mode Store
 * 
 * Manages draft mode state including:
 * - Draft vs. published mode toggle
 * - Production and draft artifact state
 * - Autosave status tracking
 * 
 * Based on LogicAppsUX Designer V2 useArtifacts pattern.
 */

import { useMemo } from 'react';
import { create } from 'zustand';
import type { WorkflowDefinition } from '@/types/workflowDefinition';
import type { ConnectionsConfiguration } from '@/types/logicApps';
import type { DraftStatus, WorkflowArtifacts } from '@/types/draftMode';
import isEqual from 'lodash.isequal';

// =============================================================================
// TYPES
// =============================================================================

export interface DraftModeState {
  // Mode
  /** Whether the designer is in draft mode (editable) vs published mode (read-only) */
  isDraftMode: boolean;

  // Production artifacts (the published version)
  prodWorkflow: WorkflowDefinition | null;
  prodConnections: ConnectionsConfiguration | null;
  prodParameters: Record<string, unknown> | null;
  prodNotes: Record<string, unknown> | null;

  // Draft artifacts (the working version)
  draftWorkflow: WorkflowDefinition | null;
  draftConnections: ConnectionsConfiguration | null;
  draftParameters: Record<string, unknown> | null;
  draftNotes: Record<string, unknown> | null;

  // Autosave state
  draftStatus: DraftStatus;
  lastSavedTime: Date | null;
  saveError: string | null;

  // Loading state
  isLoading: boolean;
  isPublishing: boolean;
}

export interface DraftModeActions {
  // Mode actions
  setIsDraftMode: (isDraft: boolean) => void;
  
  // Artifact actions
  setProdArtifacts: (artifacts: Partial<WorkflowArtifacts>) => void;
  setDraftArtifacts: (artifacts: Partial<WorkflowArtifacts>) => void;
  initializeArtifacts: (prod: WorkflowArtifacts, draft?: WorkflowArtifacts) => void;
  
  // Autosave actions
  setDraftStatus: (status: DraftStatus) => void;
  setLastSavedTime: (time: Date | null) => void;
  setSaveError: (error: string | null) => void;
  
  // Draft operations
  discardDraft: () => void;
  markDraftAsSaved: () => void;
  
  // Publishing
  setIsPublishing: (isPublishing: boolean) => void;
  
  // Computed selectors (as functions)
  areProdAndDraftEqual: () => boolean;
  getCurrentArtifacts: () => WorkflowArtifacts | null;
  
  // Reset
  reset: () => void;
}

export type DraftModeStore = DraftModeState & DraftModeActions;

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: DraftModeState = {
  isDraftMode: true, // Start in draft mode by default

  prodWorkflow: null,
  prodConnections: null,
  prodParameters: null,
  prodNotes: null,

  draftWorkflow: null,
  draftConnections: null,
  draftParameters: null,
  draftNotes: null,

  draftStatus: 'idle',
  lastSavedTime: null,
  saveError: null,

  isLoading: false,
  isPublishing: false,
};

// =============================================================================
// STORE
// =============================================================================

export const useDraftModeStore = create<DraftModeStore>((set, get) => ({
  ...initialState,
  
  // ---------------------------------------------------------------------------
  // Mode actions
  // ---------------------------------------------------------------------------
  
  setIsDraftMode: (isDraft) => {
    set({ isDraftMode: isDraft });
  },
  
  // ---------------------------------------------------------------------------
  // Artifact actions
  // ---------------------------------------------------------------------------
  
  setProdArtifacts: (artifacts) => {
    set((state) => ({
      prodWorkflow: artifacts.workflow ?? state.prodWorkflow,
      prodConnections: artifacts.connections ?? state.prodConnections,
      prodParameters: artifacts.parameters ?? state.prodParameters,
      prodNotes: artifacts.notes ?? state.prodNotes,
    }));
  },
  
  setDraftArtifacts: (artifacts) => {
    set((state) => ({
      draftWorkflow: artifacts.workflow ?? state.draftWorkflow,
      draftConnections: artifacts.connections ?? state.draftConnections,
      draftParameters: artifacts.parameters ?? state.draftParameters,
      draftNotes: artifacts.notes ?? state.draftNotes,
    }));
  },
  
  initializeArtifacts: (prod, draft) => {
    set({
      prodWorkflow: prod.workflow,
      prodConnections: prod.connections,
      prodParameters: prod.parameters ?? null,
      prodNotes: prod.notes ?? null,

      // If no draft provided, use production as draft
      draftWorkflow: draft?.workflow ?? prod.workflow,
      draftConnections: draft?.connections ?? prod.connections,
      draftParameters: draft?.parameters ?? prod.parameters ?? null,
      draftNotes: draft?.notes ?? prod.notes ?? null,

      isLoading: false,
      draftStatus: 'idle',
    });
  },
  
  // ---------------------------------------------------------------------------
  // Autosave actions
  // ---------------------------------------------------------------------------
  
  setDraftStatus: (status) => {
    set({ draftStatus: status });
  },
  
  setLastSavedTime: (time) => {
    set({ lastSavedTime: time });
  },
  
  setSaveError: (error) => {
    set({ 
      saveError: error,
      draftStatus: error ? 'error' : get().draftStatus,
    });
  },
  
  // ---------------------------------------------------------------------------
  // Draft operations
  // ---------------------------------------------------------------------------
  
  /**
   * Discard draft changes - reset draft to match production
   */
  discardDraft: () => {
    const { prodWorkflow, prodConnections, prodParameters, prodNotes } = get();
    set({
      draftWorkflow: prodWorkflow,
      draftConnections: prodConnections,
      draftParameters: prodParameters,
      draftNotes: prodNotes,
      draftStatus: 'idle',
      saveError: null,
    });
  },
  
  /**
   * Mark draft as saved - update production to match draft
   */
  markDraftAsSaved: () => {
    const { draftWorkflow, draftConnections, draftParameters, draftNotes } = get();
    set({
      prodWorkflow: draftWorkflow,
      prodConnections: draftConnections,
      prodParameters: draftParameters,
      prodNotes: draftNotes,
      draftStatus: 'saved',
      lastSavedTime: new Date(),
      saveError: null,
    });
  },
  
  // ---------------------------------------------------------------------------
  // Publishing
  // ---------------------------------------------------------------------------
  
  setIsPublishing: (isPublishing) => {
    set({ isPublishing });
  },
  
  // ---------------------------------------------------------------------------
  // Computed selectors
  // ---------------------------------------------------------------------------
  
  /**
   * Check if production and draft are equal
   */
  areProdAndDraftEqual: () => {
    const {
      prodWorkflow,
      draftWorkflow,
      prodConnections,
      draftConnections,
      prodParameters,
      draftParameters,
      prodNotes,
      draftNotes,
    } = get();

    return (
      isEqual(prodWorkflow, draftWorkflow) &&
      isEqual(prodConnections, draftConnections) &&
      isEqual(prodParameters, draftParameters) &&
      isEqual(prodNotes, draftNotes)
    );
  },
  
  /**
   * Get current artifacts based on mode
   */
  getCurrentArtifacts: () => {
    const {
      isDraftMode,
      prodWorkflow,
      draftWorkflow,
      prodConnections,
      draftConnections,
      prodParameters,
      draftParameters,
      prodNotes,
      draftNotes,
    } = get();

    const workflow = isDraftMode ? draftWorkflow : prodWorkflow;
    if (!workflow) return null;

    return {
      workflow,
      connections: isDraftMode ? draftConnections : prodConnections,
      parameters: (isDraftMode ? draftParameters : prodParameters) ?? undefined,
      notes: (isDraftMode ? draftNotes : prodNotes) ?? undefined,
    };
  },
  
  // ---------------------------------------------------------------------------
  // Reset
  // ---------------------------------------------------------------------------
  
  reset: () => {
    set(initialState);
  },
}));

// =============================================================================
// SELECTORS (for use with shallow comparison)
// =============================================================================

export const selectIsDraftMode = (state: DraftModeStore) => state.isDraftMode;
export const selectDraftStatus = (state: DraftModeStore) => state.draftStatus;
export const selectLastSavedTime = (state: DraftModeStore) => state.lastSavedTime;
export const selectSaveError = (state: DraftModeStore) => state.saveError;
export const selectIsPublishing = (state: DraftModeStore) => state.isPublishing;

/**
 * Derived selector: isReadOnly is true when not in draft mode
 */
export const selectIsReadOnly = (state: DraftModeStore) => !state.isDraftMode;

// =============================================================================
// MEMOIZED HOOKS
// =============================================================================

/**
 * Memoized selector for areProdAndDraftEqual.
 * Avoids expensive deep comparison on every render by:
 * 1. Only re-computing when the underlying state slices change
 * 2. Using shallow equality check first before deep comparison
 */
export function useAreProdAndDraftEqual(): boolean {
  const prodWorkflow = useDraftModeStore((s) => s.prodWorkflow);
  const draftWorkflow = useDraftModeStore((s) => s.draftWorkflow);
  const prodConnections = useDraftModeStore((s) => s.prodConnections);
  const draftConnections = useDraftModeStore((s) => s.draftConnections);
  const prodParameters = useDraftModeStore((s) => s.prodParameters);
  const draftParameters = useDraftModeStore((s) => s.draftParameters);
  const prodNotes = useDraftModeStore((s) => s.prodNotes);
  const draftNotes = useDraftModeStore((s) => s.draftNotes);

  return useMemo(() => {
    // Fast path: if references are identical, skip deep comparison
    if (
      prodWorkflow === draftWorkflow &&
      prodConnections === draftConnections &&
      prodParameters === draftParameters &&
      prodNotes === draftNotes
    ) {
      return true;
    }

    // Slow path: deep comparison for changed references
    return (
      isEqual(prodWorkflow, draftWorkflow) &&
      isEqual(prodConnections, draftConnections) &&
      isEqual(prodParameters, draftParameters) &&
      isEqual(prodNotes, draftNotes)
    );
  }, [
    prodWorkflow,
    draftWorkflow,
    prodConnections,
    draftConnections,
    prodParameters,
    draftParameters,
    prodNotes,
    draftNotes,
  ]);
}
